// Slider component with discrete stop points.

import { FadingView, ScalingView } from 'component/animation';
import { Center, HorizBox, HoverView, Pad, PadBox } from 'component/basics';
import {
    colorBorderPrimary,
    colorButtonAccent,
    colorButtonPrimary,
    colorButtonSliderThumb,
    colorSurfacePrimary,
    colorSurfaceSecondary,
    colorTextOnAccentColorBg,
} from 'component/color';
import { DataVizText, UtilityText } from 'component/text';
import { useTranslation } from 'component/translation';
import { NonSelectableView } from 'platform-specific/non-selectable-view';
import React, { useEffect, useRef, useState } from 'react';
import { Animated, ColorValue, Easing, PanResponder, Pressable, StyleSheet, View } from 'react-native';
import { undefN } from 'util/stdtypes';

// A slider component that snaps to the nearest stop point when released.
// A value of zero means that nothing has been selected, and the handle will be hidden.

// HACK: This component requires a few casts to any to pass typechecking
// At some point we should clean this up so it doesn't need that.

// HACK: This will only work on web. Further work will be needed if we want to support native.

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function SliderWithStopPoints({
    title,
    numberOfStops,
    optionLabels,
    optionColors,
    value,
    isDefault = false,
    onChangeValue,
    onStartDragging = () => {},
    onEndDragging = () => {},
    snapOnMove = true,
}) {
    const s = SliderWithStopPointsStyle;
    const [componentWidth, setComponentWidth] = useState(null);
    const xPosition = useRef(new Animated.Value(0)).current;
    const [hasLayout, setHasLayout] = useState(false);
    const [showHover, setShowHover] = useState(false);
    const [hoverValue, setHoverValue] = useState(0);
    const [hoverPosition, setHoverPosition] = useState(0);
    const [hasDragged, setHasDragged] = useState(false);
    const previousValue = useRef(value);
    const barRef = useRef<HTMLDivElement>(null);
    const sliderAriaLabel = useTranslation('Use left & right or up & down arrow keys to adjust slider');

    const [isDragging, setIsDragging] = useState(false);
    const [snapValue, setSnapValue] = useState(value);

    function getSpaceBetweenStops() {
        const rect = barRef.current?.getBoundingClientRect();
        return (rect?.width ?? 0 - 8) / (numberOfStops - 1);
    }

    function valueToXPosition(value) {
        const stopWidth = getSpaceBetweenStops();
        return (value - 1) * stopWidth;
    }

    function screenXPositionXOffset(xPosition) {
        const rect = barRef.current?.getBoundingClientRect();
        return xPosition - (rect?.left ?? 0);
    }

    function xPositionToValue(xPosition) {
        const stopWidth = getSpaceBetweenStops();
        const unclippedValue = Math.round(xPosition / stopWidth) + 1;
        return Math.max(1, Math.min(numberOfStops, unclippedValue));
    }

    useEffect(() => {
        const newXPosition = valueToXPosition(snapOnMove && isDragging ? snapValue : value);
        xPosition.flattenOffset();

        if ((isDragging && snapOnMove) || (previousValue.current > 0 && previousValue.current != value)) {
            Animated.spring(xPosition, {
                toValue: newXPosition,
                useNativeDriver: false,
                speed: 25,
                bounciness: 7,
            }).start();
        } else {
            xPosition.setValue(newXPosition);
        }
        previousValue.current = value;
    }, [value, componentWidth, snapValue]);

    useEffect(() => {
        const handleKeyDown = ({ key }) => {
            if (document.activeElement?.id !== barRef?.current?.id) return null;

            switch (key) {
                case 'ArrowLeft':
                    onChangeValue(Math.max(1, value - 1));
                    break;
                case 'ArrowRight':
                    onChangeValue(Math.min(numberOfStops, value + 1));
                    break;
                case 'ArrowDown':
                    onChangeValue(Math.max(1, value - 1));
                    break;
                case 'ArrowUp':
                    onChangeValue(Math.min(numberOfStops, value + 1));
                    break;
                default:
                    break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [value, onChangeValue, numberOfStops]);

    const panResponder = PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderTerminate: () => {
            onEndDragging?.();
            setIsDragging(false);
        },
        onPanResponderGrant: () => {
            xPosition.flattenOffset();
        },
        onPanResponderMove: (_, gestureState) => {
            setIsDragging(true);
            onStartDragging?.();
            const dragX = screenXPositionXOffset(gestureState.moveX);
            const rect = barRef.current?.getBoundingClientRect();
            const clippedX = Math.max(-10, Math.min(rect?.width ?? 0, dragX));
            setHasDragged(true);
            setShowHover(true);
            const newHoverValue = xPositionToValue(dragX);
            if (snapOnMove) {
                setSnapValue(newHoverValue);
            } else {
                xPosition.setValue(clippedX);
            }
            setHoverValue(newHoverValue);
            setHoverPosition(valueToXPosition(newHoverValue));
        },
        onPanResponderRelease: (_, gestureState) => {
            setIsDragging(false);
            onEndDragging?.();
            xPosition.flattenOffset();
            const pressX = screenXPositionXOffset(gestureState.moveX);
            const newValue = xPositionToValue(pressX);
            const newXPosition = valueToXPosition(newValue);
            Animated.timing(xPosition, {
                toValue: newXPosition,
                duration: 300,
                useNativeDriver: false,
            }).start();
            onChangeValue(newValue);
            setShowHover(false);

            barRef.current?.focus();
        },
    });

    const handleBarPress = (e) => {
        if ((componentWidth ?? 0) > 0 && barRef.current) {
            const pressX = screenXPositionXOffset(e.nativeEvent.pageX);
            const newValue = xPositionToValue(pressX);
            onChangeValue(newValue);
        }
    };

    const handleLayout = (event) => {
        const { width } = event.nativeEvent.layout;
        setComponentWidth(width);
        xPosition.setValue((value - 1) * (width / (numberOfStops - 1)));
        setHasLayout(true);
    };

    return (
        <NonSelectableView>
            <View>
                <DataVizText label={title} type='small' />
                <Pad size={24} />
                <View style={{ position: 'relative' }}>
                    <FadingView
                        duration={200}
                        style={{
                            position: 'absolute',
                            zIndex: 10,
                            transform: [{ translateX: xPosition }, { translateX: '-50%' }, { translateY: '-100%' }],
                        }}
                        visible={isDragging}
                    >
                        <PadBox bottom={24}>
                            <HoverTooltip alignment={(snapValue - 1) / (numberOfStops - 1)} label={optionLabels[hoverValue - 1]} />
                        </PadBox>
                    </FadingView>
                </View>
            </View>
            <View onLayout={handleLayout} style={s.barContainerOuter}>
                <Pressable
                    aria-label={sliderAriaLabel}
                    id={`barRef-${title.replace(/\s+/g, '-').toLowerCase()}`}
                    onPress={handleBarPress}
                    ref={barRef as any}
                    style={s.sliderPressableArea}
                >
                    {value > 0 && hasLayout && (
                        <Animated.View
                            {...panResponder.panHandlers}
                            style={[s.dragHandle, { transform: [{ translateX: xPosition }, { translateX: '-50%' }] }]}
                        >
                            <SliderHandle pressed={isDragging} value={!isDefault && value} />
                        </Animated.View>
                    )}
                    <PadBox horiz={4}>
                        <HorizBox center spread>
                            {Array(numberOfStops)
                                .fill(null)
                                .map((_, idx) => (
                                    <View key={idx} style={s.selectionStopPoint} />
                                ))}
                        </HorizBox>
                    </PadBox>
                </Pressable>
            </View>
        </NonSelectableView>
    );
}

const SliderWithStopPointsStyle = StyleSheet.create({
    barContainerOuter: {
        width: '100%',
        height: 10,
        borderRadius: 4,
        backgroundColor: colorBorderPrimary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    sliderPressableArea: {
        position: 'absolute',
        width: '100%',
        height: 25,
        justifyContent: 'center',
    },
    overlapBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 8,
    },
    dragHandle: {
        borderRadius: 16,
        position: 'absolute',
        cursor: 'pointer',
        zIndex: 10,
        alignItems: 'center',
        justifyContent: 'center',
    } as any,
    dragHandleShadow: {
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.30)',
    } as any,
    selectionStopPoint: {
        width: 6,
        height: 6,
        borderRadius: 4,
        backgroundColor: colorSurfacePrimary,
        borderColor: colorSurfaceSecondary,
    },
});

const SliderHandleStyle = StyleSheet.create({
    handleContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        width: 48,
        height: 48,
        borderRadius: 24,
    },
    handle: {
        width: 24,
        height: 24,
        borderRadius: 12,
        filter: 'drop-shadow(0 2px 7px rgba(0, 0, 0, 0.15));',
        borderWidth: 1,
    },
    pulseContainer: {
        position: 'absolute',
    },
    pulse: {
        transformOrigin: 'center',
        width: 24,
        height: 24,
        borderRadius: 12,
        opacity: 0.2,
        backgroundColor: colorButtonAccent,
    },
});

function SliderHandle({ value = undefN, pressed = false }) {
    const [hovering, setHovering] = useState(false);
    const [pressing, setPressing] = useState(false);
    // Internal press state, external value is set while dragging
    const pressIn = () => {
        setPressing(true);
    };
    const pressOut = () => {
        setTimeout(() => setPressing(false), 50);
    };
    const hoverIn = () => {
        setHovering(true);
    };
    const hoverOut = () => {
        setHovering(false);
    };

    const colorValue = useRef(new Animated.Value(value ? 1 : 0)).current;
    useEffect(() => {
        Animated.timing(colorValue, {
            toValue: value && !pressed && !pressing ? 1 : 0,
            duration: 200,
            useNativeDriver: false,
        }).start();
    }, [value, pressed, pressing]);

    const backgroundColor = colorValue.interpolate({
        inputRange: [0, 1],
        outputRange: [colorButtonSliderThumb, colorButtonAccent],
    });

    const borderColor = colorValue.interpolate({
        inputRange: [0, 1],
        outputRange: [colorBorderPrimary, colorButtonAccent],
    });

    return (
        <View>
            <Pressable
                accessible={false}
                focusable={false}
                importantForAccessibility='no-hide-descendants'
                onHoverIn={hoverIn}
                onHoverOut={hoverOut}
                onPressIn={pressIn}
                onPressOut={pressOut}
                style={SliderHandleStyle.handleContainer}
                tabIndex={-1}
            >
                {!value && !pressed && (
                    <View style={SliderHandleStyle.pulseContainer}>
                        <HandlePulse />
                    </View>
                )}
                <ScalingView
                    duration={400}
                    scale={pressing || pressed ? 0.9 : hovering ? 1.1 : 1}
                    style={[SliderHandleStyle.handle, { backgroundColor: backgroundColor as any, borderColor: borderColor as any }]}
                />
            </Pressable>
        </View>
    );
}

function HandlePulse() {
    const scaleValue = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(scaleValue, {
                    toValue: 1.8,
                    duration: 600,
                    easing: Easing.inOut(Easing.quad),
                    useNativeDriver: true,
                }),
                Animated.timing(scaleValue, {
                    toValue: 1,
                    duration: 600,
                    easing: Easing.inOut(Easing.quad),
                    useNativeDriver: true,
                }),
            ]),
        ).start();
    }, [scaleValue]);

    return (
        <Animated.View
            style={[
                SliderHandleStyle.pulse,
                {
                    transform: [{ scale: scaleValue }],
                },
            ]}
        />
    );
}

const HoverTooltipStyle = StyleSheet.create({
    container: {
        backgroundColor: colorButtonPrimary,
        borderRadius: 8,
        padding: 8,
        zIndex: 10,
    },
    tip: {
        bottom: 0,
        position: 'absolute',
        transform: [{ translateY: '+50%' }],
        color: colorButtonPrimary,
    },
});

function HoverTooltip({ label, alignment = 0.5 }) {
    const offsetValue = useRef(new Animated.Value(alignment)).current;
    useEffect(() => {
        Animated.timing(offsetValue, {
            toValue: alignment,
            duration: 200,
            useNativeDriver: false,
        }).start();
    }, [alignment]);

    return (
        <Center>
            <PadBox horiz={20}>
                <Animated.View style={{ transform: [{ translateX: `${-alignment * 100 + 50}%` }, { translateX: 40 * (alignment - 0.5) }] }}>
                    <View style={HoverTooltipStyle.container}>
                        <Center>
                            <UtilityText color={colorTextOnAccentColorBg} label={label} type='tiny' weight='medium' />
                        </Center>
                    </View>
                </Animated.View>
            </PadBox>
            <View style={HoverTooltipStyle.tip}>
                <HoverTip />
            </View>
        </Center>
    );
}

function HoverTip() {
    return (
        <svg fill='none' height='18' viewBox='0 0 24 18' width='24' xmlns='http://www.w3.org/2000/svg'>
            <path
                d='M13.5848 16.5409C12.7842 17.5809 11.2158 17.5809 10.4152 16.5409L0.622342 3.8196C-0.390052 2.50446 0.547477 0.59961 2.20715 0.599611L21.7928 0.599613C23.4525 0.599613 24.3901 2.50446 23.3777 3.8196L13.5848 16.5409Z'
                fill={colorButtonPrimary}
            />
        </svg>
    );
}
